import { createContext, ReactNode, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { useAuth } from 'react-oidc-context'
import UserService from '../../services/user'
import { calculateProfileScore, hasPermission, isSupervisorProfile } from '../../helpers/permissionUtils'

const normalizeProfileId = (id: unknown): string => (id === undefined || id === null ? '' : String(id))

const normalizeRoles = (roles: unknown): string[] => {
  if (!Array.isArray(roles)) return []
  const out: string[] = []
  for (const r of roles) {
    if (typeof r === 'string') {
      const t = r.trim()
      if (t) out.push(t)
    }
  }
  return out
}

/**
 * Identidade do usuário em texto, para decidir se a revalidação do userinfo mudou algo.
 * Bug 872092: quando nada mudou, o objeto anterior é mantido e os consumidores do contexto
 * não re-renderizam a cada renovação do token.
 */
const assinaturaUsuario = (u: UserProps | null | undefined): string =>
  !u
    ? ''
    : JSON.stringify([
        u.sub ?? '',
        u.name ?? '',
        u.preferred ?? '',
        u.foto ?? '',
        u.token,
        u.activeProfile?.id ?? '',
        (u.profiles ?? []).map(p => [p.id, p.name, [...p.roles].sort()]),
      ])

const isAuthDebugEnabled = (): boolean => {
  try {
    return (
      process.env.REACT_APP_DEBUG_AUTH === 'true' ||
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('sgo-debug-auth') === '1')
    )
  } catch {
    return false
  }
}

const logAuthDebug = (message: string, data?: unknown) => {
  if (!isAuthDebugEnabled()) return
  // eslint-disable-next-line no-console
  console.info('[SGO Auth Debug]', message, data ?? '')
}

type UserProfile = {
  id: string
  name: string
  title: string
  roles: string[]
  isActive: boolean
  priority?: number
}

type UserProps = {
  name?: string
  sub?: string
  token: string
  foto?: string
  preferred?: string
  profiles?: UserProfile[]
  activeProfile?: UserProfile
}

type AuthContextProps = {
  user: UserProps | null
  isLoading: boolean
  switchProfile: (profileId: string) => void
  getMostComprehensiveProfile: () => UserProfile | null
  handleHasPermission: (permission: string) => boolean
}

const AuthContext = createContext<AuthContextProps | null>(null)

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserProps | null>(null)
  const [isLoading, setLoading] = useState<boolean>(true)
  const [resolvedToken, setResolvedToken] = useState<string | undefined>()
  // Bug 872092: o efeito abaixo roda a cada renovacao do access token e precisa saber se
  // ja existe usuario carregado; ler o state ali dentro o colocaria nas dependencias e o
  // faria disparar de novo a cada setUser.
  const userRef = useRef<UserProps | null>(null)

  const { user: userAuth, isLoading: authLoading } = useAuth()

  const calculateMostComprehensiveProfile = (profiles: UserProfile[]): UserProfile | null => {
    if (!profiles || profiles.length === 0) return null

    let best: UserProfile | null = null
    let bestScore = -1

    for (const profile of profiles) {
      const score = calculateProfileScore(profile)
      const tieBreak = profile.roles.length > (best?.roles.length ?? 0)
      if (score > bestScore || (score === bestScore && tieBreak)) {
        bestScore = score
        best = profile
      }
    }

    if (!best) return null

    // IMPORTANTE: nao mesclar roles de perfis diferentes.
    // Perfil escolhido pelo score (admin > atendimento > formulario), nao apenas pela contagem bruta de roles.
    return { ...best, isActive: true }
  }

  const switchProfile = useCallback((profileId: string) => {
    setUser((prev) => {
      if (!prev?.profiles) {
        return prev
      }

      const newProfile = prev.profiles.find(p => String(p.id) === String(profileId))
      
      if (!newProfile) {
        logAuthDebug('switchProfile: perfil nao encontrado', {
          profileId,
          idsDisponiveis: prev.profiles.map((p) => p.id),
        })
        return prev
      }
      
      const updatedProfiles = prev.profiles.map(profile => ({
        ...profile,
        isActive: String(profile.id) === String(profileId)
      }))

      const updatedActiveProfile = { ...newProfile, isActive: true }
      
      sessionStorage.setItem('sgo-active-profile-id', String(profileId))
      
      return {
        ...prev,
        profiles: updatedProfiles,
        activeProfile: updatedActiveProfile
      }
    })
  }, [])

  const ensureValidProfileSaved = () => {
    if (user?.activeProfile) {
      const savedProfileId = sessionStorage.getItem('sgo-active-profile-id')
      if (!savedProfileId || String(savedProfileId) !== String(user.activeProfile.id)) {
        sessionStorage.setItem('sgo-active-profile-id', String(user.activeProfile.id))
      }
    }
  }

  const getMostComprehensiveProfile = (): UserProfile | null => {
    if (authLoading || user?.token !== userAuth?.access_token || !user?.profiles) return null
    return calculateMostComprehensiveProfile(user.profiles)
  }

  const accessToken = userAuth?.access_token
  const subject = userAuth?.profile?.sub

  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    if (authLoading) {
      setLoading(true)
      return
    }
    if (!accessToken) {
      setUser(null)
      setResolvedToken(undefined)
      sessionStorage.removeItem('sgo-access-token')
      sessionStorage.removeItem('sgo-active-profile-id')
      setLoading(false)
      return
    }

    const controller = new AbortController()
    let vigente = true
    // BaseService usa este bearer; nenhuma claim é extraída do JWT no frontend.
    sessionStorage.setItem('sgo-access-token', accessToken)

    // Bug 872092: a renovação silenciosa do access token troca `accessToken` de tempos em
    // tempos. Derrubar o usuário aqui fazia o app inteiro remontar no meio do trabalho, o
    // que o usuário percebia como refresh automático da página. Na renovação do MESMO
    // usuário o token é trocado no lugar e os perfis já carregados continuam valendo; o
    // userinfo é revalidado em segundo plano, sem tela de carregamento.
    const vigenteAnterior = userRef.current
    const renovacao = Boolean(vigenteAnterior && (!subject || vigenteAnterior.sub === subject))

    if (renovacao) {
      setUser(prev => (prev ? { ...prev, token: accessToken } : prev))
      setResolvedToken(accessToken)
      setLoading(false)
    } else {
      setUser(null)
      setLoading(true)
    }

    void (async () => {
      try {
        const { data: payload } = await UserService.getUserSSO(controller.signal)
        if (!vigente) return
        if (!payload?.sub || (subject && payload.sub !== subject)) {
          throw new Error('Identidade retornada pelo userinfo não corresponde à sessão')
        }
        const groups = Array.isArray(payload.groups) ? payload.groups : payload.profiles
        const availableProfiles: UserProfile[] = (Array.isArray(groups) ? groups : [])
          .filter((item): item is Exclude<typeof item, string> => Boolean(item && typeof item === 'object'))
          .filter(item => item.id != null && Array.isArray(item.roles))
          .map((item, index) => ({
            id: normalizeProfileId(item.id),
            name: item.name || `Perfil ${index + 1}`,
            title: item.name || `Perfil ${index + 1}`,
            roles: normalizeRoles(item.roles),
            isActive: false,
          }))
        const savedId = sessionStorage.getItem('sgo-active-profile-id')
        const selected = availableProfiles.find(profile => profile.id === savedId)
          || calculateMostComprehensiveProfile(availableProfiles)
        const activeProfile = selected ? { ...selected, isActive: true } : undefined
        if (activeProfile) sessionStorage.setItem('sgo-active-profile-id', activeProfile.id)
        else sessionStorage.removeItem('sgo-active-profile-id')
        const proximo: UserProps = {
          name: payload.name,
          sub: payload.sub,
          preferred: payload.preferred_username,
          foto: payload.foto?.replace(/^http:\/\//, 'https://'),
          token: accessToken,
          profiles: availableProfiles.map(profile => ({ ...profile, isActive: profile.id === activeProfile?.id })),
          activeProfile,
        }
        setUser(prev => (assinaturaUsuario(prev) === assinaturaUsuario(proximo) ? prev : proximo))
      } catch {
        if (!vigente) return
        // Falha de userinfo derruba a sessão também na renovação: é a regra de segurança
        // coberta por 'erro no refresh não mantém as permissões antigas'.
        setUser(null)
        sessionStorage.removeItem('sgo-active-profile-id')
        logAuthDebug('Não foi possível carregar os perfis do userinfo')
      } finally {
        if (vigente) { setResolvedToken(accessToken); setLoading(false) }
      }
    })()
    return () => {
      vigente = false
      controller.abort()
    }
  }, [accessToken, subject, authLoading])

  useEffect(() => {
    if (user?.activeProfile) {
      ensureValidProfileSaved()
    }
  }, [user?.activeProfile])

  /*
   * #872092, segunda parte. A correcao anterior tratou o efeito que monta o usuario, mas o
   * valor exposto aqui continuava derrubando a sessao por um render durante a renovacao
   * silenciosa: `accessToken` ja e o novo e `user.token` / `resolvedToken` ainda sao o
   * anterior, porque quem os atualiza e o efeito, que so roda depois da pintura. Nesse render
   * o contexto entregava `user` nulo e `isLoading` verdadeiro, e o ProtectedRoute trocava a
   * tela pela de carregamento — a arvore inteira desmontava e o que estava preenchido se
   * perdia. E o que o usuario via como refresh automatico.
   *
   * Renovacao e quando ja existe usuario carregado para o MESMO subject: ali o token e so
   * mais novo, as permissoes continuam valendo e nao ha nada a esperar. O userinfo segue
   * sendo revalidado em segundo plano pelo efeito, e a sessao continua caindo se ele falhar
   * ou se a identidade mudar — o caminho de seguranca nao passa por aqui.
   */
  const renovandoMesmoUsuario = Boolean(
    user && accessToken && user.token !== accessToken && (!subject || user.sub === subject)
  )

  const contextValue: AuthContextProps = {
    user: !authLoading && (user?.token === accessToken || renovandoMesmoUsuario) ? user : null,
    isLoading:
      authLoading ||
      (!renovandoMesmoUsuario &&
        (isLoading || Boolean(accessToken && resolvedToken !== accessToken))),
    switchProfile,
    getMostComprehensiveProfile,
    handleHasPermission: () => false,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      <>{children}</>
    </AuthContext.Provider>
  )
}

const useAuthContext = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }

  const { user, isLoading, switchProfile, getMostComprehensiveProfile } = context

  const handleHasPermission = (permission: string): boolean => {
    if (user && user.activeProfile?.roles.length) {
      if (isSupervisorProfile(user.activeProfile.name || user.activeProfile.title)) return true
      return hasPermission(user.activeProfile.roles, permission)
    }
    return false
  }

  return { user, handleHasPermission, isLoading, switchProfile, getMostComprehensiveProfile }
}

export { AuthProvider, AuthContext, useAuthContext }
